import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VehiculoFormTabs from '../../../components/vehiculos/VehiculoFormTabs.jsx';
import VehiculoGeneralForm from '../../../components/vehiculos/VehiculoGeneralForm.jsx';
import VehiculoDocsForm from '../../../components/vehiculos/VehiculoDocsForm.jsx';
import VehiculoMaintForm from '../../../components/vehiculos/VehiculoMaintForm.jsx';
import VehiculoComercialForm from '../../../components/vehiculos/VehiculoComercialForm.jsx';
import StickyActions from '../../../components/common/StickyActions.jsx';
import { createVehiculo, listVehiculos } from '../../../services/vehiculos.js';
import '../../../components/vehiculos/vehiculos.css';

const INITIAL_GENERAL = {
  patente: '',
  marca: '',
  modelo: '',
  anio: '',
  tipo: '',
  chasis: '',
  motor: '',
  capacidadKg: '',
  volumenM3: '',
  odometro: '',
  choferAsignado: '',
  empresa: '',
};

const INITIAL_DOCS = {
  vtvNumero: '',
  vtvEmision: '',
  vtvVencimiento: '',
  vtvAvisoDias: 30,
  seguroCompania: '',
  seguroPoliza: '',
  seguroVencimiento: '',
  seguroFranquicia: '',
  cedulaVerdeVto: '',
  cedulaAzulVto: '',
  adjuntos: [],
};

const INITIAL_MANT = {
  proximoServiceKm: '',
  proximoServiceFecha: '',
  estadoNeumaticos: '',
  rotacionKm: '',
  observaciones: '',
};

const INITIAL_COMERCIAL = {
  costoKm: '',
  costoHora: '',
  costoFijoMensual: '',
  propietario: '',
  choferAsignado: '',
};

function validatePatente(value) {
  const normalized = value?.trim() ?? '';
  if (!normalized) {
    return 'Ingresá la patente.';
  }
  if (!/^[A-Z0-9]{6,8}$/.test(normalized)) {
    return 'Formato de patente inválido.';
  }
  return '';
}

function validateGeneral(data) {
  const errors = {};
  const patenteError = validatePatente(data.patente);
  if (patenteError) {
    errors.patente = patenteError;
  }
  if (!data.odometro && data.odometro !== 0) {
    errors.odometro = 'Indicá el odómetro actual.';
  } else if (Number(data.odometro) < 0) {
    errors.odometro = 'El odómetro no puede ser negativo.';
  }
  return errors;
}

function validateDocs(data) {
  const errors = {};
  if (!data.vtvVencimiento) {
    errors.vtvVencimiento = 'Definí el vencimiento de la VTV.';
  }
  if (!data.seguroVencimiento) {
    errors.seguroVencimiento = 'Definí el vencimiento del seguro.';
  }
  if (data.vtvEmision && data.vtvVencimiento && data.vtvEmision > data.vtvVencimiento) {
    errors.vtvVencimiento = 'El vencimiento debe ser posterior a la emisión.';
  }
  if (data.vtvAvisoDias && Number(data.vtvAvisoDias) < 0) {
    errors.vtvAvisoDias = 'Debe ser positivo.';
  }
  return errors;
}

function validateComercial(data) {
  const errors = {};
  ['costoKm', 'costoHora', 'costoFijoMensual'].forEach(field => {
    const value = data[field];
    if (value === '') {
      return;
    }
    if (Number(value) < 0) {
      errors[field] = 'Debe ser mayor o igual a 0.';
    }
  });
  return errors;
}

function normalizeNumber(value) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }
  return Number(value);
}

export default function AltaVehiculo() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [general, setGeneral] = useState(() => ({ ...INITIAL_GENERAL }));
  const [documentacion, setDocumentacion] = useState(() => ({ ...INITIAL_DOCS, adjuntos: [] }));
  const [mantenimiento, setMantenimiento] = useState(() => ({ ...INITIAL_MANT }));
  const [comercial, setComercial] = useState(() => ({ ...INITIAL_COMERCIAL }));
  const [activo, setActivo] = useState(true);
  const [errors, setErrors] = useState({ general: {}, documentacion: {}, comercial: {}, mantenimiento: {} });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const resumen = useMemo(() => ({ patente: general.patente || '—', tipo: general.tipo || '—' }), [general]);

  const handleSubmit = async () => {
    const generalErrors = validateGeneral(general);
    const docsErrors = validateDocs(documentacion);
    const comercialErrors = validateComercial(comercial);
    const mantenimientoErrors = {};

    setErrors({ general: generalErrors, documentacion: docsErrors, comercial: comercialErrors, mantenimiento: mantenimientoErrors });

    const firstErrorTab = ['general', 'documentacion', 'comercial'].find(tab => {
      const map = { general: generalErrors, documentacion: docsErrors, comercial: comercialErrors, mantenimiento: mantenimientoErrors };
      return Object.keys(map[tab]).length > 0;
    });

    if (firstErrorTab) {
      setActiveTab(firstErrorTab);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const existing = await listVehiculos({ search: general.patente });
      const alreadyExists = existing.data?.some(item => item.patente === general.patente);
      if (alreadyExists) {
        setSubmitError('Ya existe un vehículo con esa patente.');
        setActiveTab('general');
        return;
      }
      const payload = {
        ...general,
        odometro: normalizeNumber(general.odometro),
        capacidadKg: normalizeNumber(general.capacidadKg),
        volumenM3: normalizeNumber(general.volumenM3),
        anio: general.anio ? Number(general.anio) : null,
        documentacion: {
          ...documentacion,
          vtvAvisoDias: Number(documentacion.vtvAvisoDias || 0),
        },
        mantenimiento: {
          ...mantenimiento,
          proximoServiceKm: mantenimiento.proximoServiceKm === '' ? '' : Number(mantenimiento.proximoServiceKm),
          rotacionKm: mantenimiento.rotacionKm === '' ? '' : Number(mantenimiento.rotacionKm),
        },
        comercial: {
          ...comercial,
          costoKm: comercial.costoKm === '' ? 0 : Number(comercial.costoKm),
          costoHora: comercial.costoHora === '' ? 0 : Number(comercial.costoHora),
          costoFijoMensual: comercial.costoFijoMensual === '' ? 0 : Number(comercial.costoFijoMensual),
        },
        activo,
      };

      const response = await createVehiculo(payload);
      if (response.ok) {
        setSubmitResult({ id: response.id, patente: payload.patente });
        setGeneral({ ...INITIAL_GENERAL });
        setDocumentacion({ ...INITIAL_DOCS, adjuntos: [] });
        setMantenimiento({ ...INITIAL_MANT });
        setComercial({ ...INITIAL_COMERCIAL });
        setActivo(true);
        setActiveTab('general');
      }
    } catch (error) {
      console.error(error);
      setSubmitError('Ocurrió un error inesperado al guardar.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vehiculos-layout">
      <div className="vehiculos-header">
        <div className="vehiculos-header__title">
          <h1>Alta de vehículo</h1>
          <span className="vehiculos-header__subtitle">Completá los datos para sumar un vehículo a la flota.</span>
        </div>
        <div className="vehiculos-header__subtitle">Resumen: {resumen.patente} · {resumen.tipo}</div>
      </div>

      {submitResult && (
        <div className="alerta-banner" role="status">
          <div>
            <strong>Vehículo guardado</strong>
            <span>
              ID: {submitResult.id} – {submitResult.patente}. Podés verlo en el listado o cargar otro vehículo.
            </span>
          </div>
          <div className="table-actions">
            <button type="button" onClick={() => navigate(`/administracion/vehiculos/${submitResult.id}`)}>
              Ver detalle
            </button>
            <button type="button" onClick={() => setSubmitResult(null)}>
              Ocultar
            </button>
          </div>
        </div>
      )}

      {submitError && <p className="field-error">{submitError}</p>}

      <VehiculoFormTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'general' && (
        <VehiculoGeneralForm value={general} onChange={setGeneral} errors={errors.general} />
      )}

      {activeTab === 'documentacion' && (
        <VehiculoDocsForm value={documentacion} onChange={setDocumentacion} errors={errors.documentacion} />
      )}

      {activeTab === 'mantenimiento' && (
        <VehiculoMaintForm value={mantenimiento} onChange={setMantenimiento} errors={errors.mantenimiento} />
      )}

      {activeTab === 'comercial' && (
        <VehiculoComercialForm
          value={comercial}
          onChange={setComercial}
          errors={errors.comercial}
          activo={activo}
          onActivoChange={setActivo}
        />
      )}

      <StickyActions
        onCancel={() => navigate('/administracion/vehiculos')}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        primaryLabel="Guardar vehículo"
        secondaryLabel="Volver"
      />
    </div>
  );
}
